/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useEffect, useState } from "react";

import * as React from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { signOutUser, useCurrentUser } from "actions/CurrentUserActions";
import ParticipantAvatar from "components/ParticipantAvatar";
import { useProfile } from "hooks/useProfile";
import userIconUrl from "images/user-icon.svg";
import { PaddedPopover } from "components/shared/PaddedPopover";
import { UpgradeButtonCallbackOnly } from "components/shared/UpgradeButton";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { EnhanceSpeechQuota } from "components/shared/EnhanceSpeechQuota";
import {
  useUserMetrics,
  UserMetrics,
} from "components/ProjectIndex/useUserMetrics";
import { OverlayTrigger } from "@spectrum-web-components/overlay";
import { useEventListener } from "hooks/useEventListener";
import { addToast } from "actions/ToastMessageActions";
import { LanguagePicker } from "components/shared/LanguagePicker";
import { webComponentShim } from "components/shared/WebComponentShim";

const LANGUAGE_PICKER_ENABLED = process.env.LANGUAGE_PICKER_ENABLED === "true";

const AvatarButtonLink = styled.a`
  cursor: pointer;
  color: var(--spectrum-gray-800);
  text-decoration: none;
`;

const ProfileName = styled.div`
  font: normal normal 900 22px/27px ${fontFamilies};
  color: black;
  margin-bottom: 16px;
`;

const UpgradeButtonMobileContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: start;
`;

const ProfileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  margin-left: 5px;
  margin-bottom: 24px;
`;

const ProfileDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const ProfileImg = styled.img`
  width: 18px;
  height: 18px;
`;

const ProfileHyperlink = styled.a`
  font: normal normal normal 18px/22px ${fontFamilies};
  text-decoration: none;
  color: black;
`;

const ProfilePanel: React.FC<{
  close: VoidFunction;
  userMetrics: UserMetrics;
  isUserMetricsLoading: boolean;
  isFetchErrorOccurred: boolean;
  onUpgradeInvoked: (skippedToCheckout: boolean) => void;
}> = ({
  close,
  userMetrics,
  isUserMetricsLoading,
  isFetchErrorOccurred,
  onUpgradeInvoked,
}) => {
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();
  const { profile } = useProfile(currentUser?.adobeId);
  const isMobile = useIsMobile();

  const onSignoutClicked = useCallback(() => {
    dispatch(signOutUser());
  }, [dispatch]);

  const onUpgradeButtonClicked = (skippedToCheckout: boolean): void => {
    onUpgradeInvoked(skippedToCheckout);
    close();
  };

  return (
    <>
      <ProfileName className="fs-mask">{profile?.displayName}</ProfileName>
      <EnhanceSpeechQuota
        isUserMetricsLoading={isUserMetricsLoading}
        isFetchErrorOccurred={isFetchErrorOccurred}
        userMetrics={userMetrics}
      />
      <ProfileLinks>
        <ProfileDiv>
          <ProfileImg src={userIconUrl} alt="" />
          <ProfileHyperlink target="_blank" href={process.env.ACCOUNTS_PAGE}>
            {I18n.t("login.account")}
          </ProfileHyperlink>
        </ProfileDiv>
        {LANGUAGE_PICKER_ENABLED && (
          <ProfileDiv>
            <LanguagePicker />
          </ProfileDiv>
        )}
      </ProfileLinks>
      {isMobile && (
        <UpgradeButtonMobileContainer>
          <UpgradeButtonCallbackOnly
            onClick={onUpgradeButtonClicked}
            position={
              ExpressCheckoutButtonPosition.UPGRADE_BUTTON_POPUP_PROFILE_DROPDOWN
            }
          />
        </UpgradeButtonMobileContainer>
      )}
      <sp-button variant="secondary" size="xl" onClick={onSignoutClicked}>
        {I18n.t("profile_popover.sign_out")}
      </sp-button>
    </>
  );
};

const SignInButton = styled(webComponentShim("sp-button")).attrs({
  size: "l",
  variant: "secondary",
})`
  ${devices.small} {
    width: 100%;
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AvatarContainer = styled.div`
  display: flex;
`;

export const ProfileDropdown: React.FC<{
  onUpgradeInvoked: (skippedToCheckout: boolean) => void;
}> = ({ onUpgradeInvoked }) => {
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const {
    userMetrics,
    refresh: refreshUserMetrics,
    isLoading: isUserMetricsLoading,
    isError: isFetchErrorOccurred,
  } = useUserMetrics();

  useEffect(() => {
    async function refreshMetricsAsync(): Promise<void> {
      try {
        await refreshUserMetrics();
      } catch (error) {
        dispatch(
          addToast({ message: I18n.t("home.user_metrics.failed_toast") })
        );
      }
    }

    if (isOpen) {
      refreshMetricsAsync();
    }
  }, [refreshUserMetrics, isOpen, dispatch]);

  useEffect(() => {
    // Override the background color of the fullscreen takeover dialog and button size
    // No way to do this in CSS because the dialog is in a shadow root. This is the way.
    const dialogWrapper = document.querySelector("sp-dialog-wrapper");

    if (dialogWrapper?.shadowRoot) {
      const style = document.createElement("style");
      style.textContent = `
        .modal.fullscreenTakeover {
          background-color: var(--spectrum-global-color-static-white) !important;
        }
        .modal.fullscreenTakeover sp-button {
          font-size: 16px;
          padding: 3px 27px;
          border-radius: 24px;
        }
      `;
      dialogWrapper.shadowRoot.appendChild(style);
    }
  }, []);

  const [overlayRef, setOverlayRef] = useState<OverlayTrigger | null>(null);
  useEventListener("sp-closed", () => setIsOpen(false), overlayRef);
  useEventListener("cancel", () => setIsOpen(false), overlayRef);
  const attachRef = useCallback(
    (node: OverlayTrigger) => setOverlayRef(node),
    []
  );

  const signIn = useCallback(() => {
    window.adobeIMS?.signIn();
  }, []);

  if (!currentUser) {
    return (
      <RightActions>
        <SignInButton onClick={signIn}>{I18n.t("login.sign_in")}</SignInButton>
      </RightActions>
    );
  }

  // TODO: Extract this here to avoid duplication with the ProfilePanel
  // const ProfilePanelWrapper = isMobile ? (
  //   <sp-dialog-wrapper
  //     slot="click-content"
  //     mode="fullscreenTakeover"
  //     cancel-label={I18n.t("profile_popover.close")}
  //   />
  // ) : (
  //   <PaddedPopover />
  // );

  return (
    <RightActions>
      <overlay-trigger
        ref={attachRef}
        placement={isMobile ? undefined : "top-start"}
        open={isOpen ? "click" : undefined}
      >
        <AvatarContainer
          data-testid="user-profile-dropdown-button"
          slot="trigger"
          onClick={(): void => setIsOpen(!isOpen)}
        >
          <AvatarButtonLink
            href="#"
            onClick={(e) => e.preventDefault()}
            aria-label={I18n.t("profile_popover.button")}
          >
            <ParticipantAvatar adobeId={currentUser?.adobeId} />
          </AvatarButtonLink>
        </AvatarContainer>
        {isMobile ? (
          <sp-dialog-wrapper
            slot="click-content"
            mode="fullscreenTakeover"
            cancel-label={I18n.t("profile_popover.close")}
          >
            <ProfilePanel
              close={(): void => setIsOpen(false)}
              userMetrics={userMetrics}
              isUserMetricsLoading={isUserMetricsLoading}
              isFetchErrorOccurred={isFetchErrorOccurred}
              onUpgradeInvoked={onUpgradeInvoked}
            />
          </sp-dialog-wrapper>
        ) : (
          <PaddedPopover>
            <ProfilePanel
              close={(): void => setIsOpen(false)}
              userMetrics={userMetrics}
              isUserMetricsLoading={isUserMetricsLoading}
              isFetchErrorOccurred={isFetchErrorOccurred}
              onUpgradeInvoked={onUpgradeInvoked}
            />
          </PaddedPopover>
        )}
      </overlay-trigger>
    </RightActions>
  );
};
